import React, { useRef, useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
} from '@mui/material';
import {
  Print as PrintIcon,
  Email as EmailIcon,
  Close as CloseIcon,
  DescriptionOutlined as DescriptionOutlinedIcon,
} from '@mui/icons-material';
import QRCode from 'qrcode';
import { useProductosMaterialContext } from 'context';
import { useAmbienteEcf, ambienteEcfValido } from 'lib/ambienteEcf';
import { FP, fpInputSx } from 'components/financepro';
import { hoyLocal } from 'lib/fechaLocal';
import { obtenerPlantilla } from './PlantillasFactura';
import DocumentoEmpresarialPreview from './DocumentoEmpresarialPreview';
import { generarHtmlDocumentoEmpresarial, money, fechaEmisionDgii, resolverFechaVencimiento } from './documentoEmpresarial';

// Ambiente e-CF de la DGII (TesteCF / CerteCF / ecf). Configurable por entorno.

const CONFIGURACION_DEFAULT = {
  plantillaPorDefecto: '80mm-basica',
  leyendaPiePagina: '',
  mensajeGracias: 'Gracias por su compra',
  mensajeGarantia: '',
  mensajeDevolucion: '',
  mostrarInfoEmpresa: true,
  nombreEmpresa: '',
  direccionEmpresa: '',
  telefonoEmpresa: '',
  emailEmpresa: '',
  rncEmpresa: '',
  sitioWebEmpresa: '',
  mostrarNCF: true,
  mostrarObservaciones: true,
  mostrarDescuentos: true,
  mostrarImpuestos: true,
  mostrarSubtotalAntesDescuentos: true,
  cortarPapelAutomatico: false,
  imprimirLogo: false,
  rutaLogo: '',
  mostrarCodigoBarras: false,
  mostrarQR: false,
  textoQR: '',
};

const RNC_CONSUMIDOR_GENERICO = '999999';

const resolverRncComprador = (factura = {}) => {
  const candidatos = [factura.rnc, factura.rnc_comprador, factura.suplidor];
  for (const c of candidatos) {
    const v = String(c || '').trim();
    if (v && v !== RNC_CONSUMIDOR_GENERICO) return v;
  }
  return '';
};

const resolverNombreCliente = (factura = {}) =>
  String(factura.cliente_nombre || factura.nombre_cliente || '').trim();

/**
 * VistaFactura: presenta la factura en pantalla con opciones de imprimir y enviar por email.
 * La configuración de impresión se obtiene del backend (tabla configuracion_factura).
 */
const VistaFactura = ({ open, onClose, facturaData = {} }) => {
  const { controller, showSuccess, showError } = useProductosMaterialContext();
  const {
    usuarioLog,
    claveLog,
    nombreNegocio,
    direccion_empresa: direccionEmpresaCtx,
    rnc_empresa: rncEmpresaCtx,
  } = controller;
  const servidor = process.env.REACT_APP_SERVIDOR || '';

  /**
   * Datos de empresa provenientes EXCLUSIVAMENTE del contexto (poblados en el login).
   * El nombre, la dirección y el RNC NO se toman del backend ni de `facturaData.empresa`:
   * si el contexto no los provee, se renderiza vacío (excepto el nombre que cae a 'Empresa').
   * Sólo se consideran cadenas no vacías y distintas del sentinel "false" que usa el
   * contexto como valor inicial.
   *
   * Excepción: `facturaData.emisor` ({ nombre, rnc, direccion, telefono }) manda
   * sobre el contexto. Lo pasa la factura electrónica de chasis cuando el
   * módulo fiscal multi-empresa emite con otra empresa: su RNC va también en
   * el QR del e-CF.
   */
  const texto = (v) => (typeof v === 'string' && v.trim() && v !== 'false' ? v.trim() : '');
  const emisorFactura = facturaData.emisor || {};
  const nombreEmpresaContexto = texto(emisorFactura.nombre) || texto(nombreNegocio);
  const direccionEmpresaContexto = texto(emisorFactura.direccion) || texto(direccionEmpresaCtx);
  const rncEmpresaContexto = texto(emisorFactura.rnc) || texto(rncEmpresaCtx);
  const telefonoEmisor = texto(emisorFactura.telefono);

  const printRef = useRef(null);
  const [dialogoEmailAbierto, setDialogoEmailAbierto] = useState(false);
  const [emailDestino, setEmailDestino] = useState('');
  const [enviandoEmail, setEnviandoEmail] = useState(false);
  const [configuracion, setConfiguracion] = useState(CONFIGURACION_DEFAULT);
  const [cargandoConfig, setCargandoConfig] = useState(false);

  const {
    items = [],
    impuestos = [],
    subtotal = 0,
    totalDescuentos = 0,
    totalImpuestos = 0,
    total = 0,
    documento = '',
    numero = '',
    fecha = hoyLocal(),
    ncf = '',
    tipoDocumento = '',
    observaciones = '',
    empresa = {},
  } = facturaData;
  
  
  // Monto exento de ITBIS (p. ej. la placa en la factura de chasis). Solo lo
  // pasan quienes lo calculan; las facturas del POS no cambian.
  const montoExento = Number(facturaData.montoExento) || 0;
  const rncComprador = resolverRncComprador(facturaData);
  const clienteNombre = resolverNombreCliente(facturaData);
  const parteCliente =
    clienteNombre || rncComprador
      ? {
          titulo: 'Cliente',
          nombre: clienteNombre || `RNC ${rncComprador}`,
          lineas: rncComprador && clienteNombre ? [`RNC: ${rncComprador}`] : [],
        }
      : null;
  const totalNotaCredito =
    (facturaData.totalNotaCredito ??
      facturaData.total_notas_credito ??
      (typeof facturaData.pago?.pagos?.nota_credito === 'number'
        ? facturaData.pago.pagos.nota_credito
        : 0)) ?? 0;
  const totalAPagar = Math.max(0, Number(total) - Number(totalNotaCredito));

  // Comprobante fiscal electronico: el QR de la DGII y la fecha de validez del
  // NCF solo se muestran cuando el emisor lo indica ('S'). Otros invocadores de
  // VistaFactura que no envian este flag conservan su comportamiento original.
  const esElectronico =
    String(facturaData.comprobanteelectronico || '').toUpperCase() === 'S';

  // Fecha de vencimiento del NCF. En E32 (consumo) menor a 250,000 no se
  // muestra, igual que el timbre FC / la impresión térmica.
  const fechaVencimiento = useMemo(() => {
    const ncfQr = String(ncf || facturaData.comprobante || '').toUpperCase();
    const codigo = String(facturaData.codigocomprobante ?? '').trim();
    const esConsumo = ncfQr.slice(0, 3) === 'E32' || codigo === '32';
    const montoFuente = facturaData.monto_total_ecf ?? (total || 0);
    if (esConsumo && Number(montoFuente) < 250000) return '';
    return resolverFechaVencimiento(facturaData);
  }, [facturaData, ncf, total]);

  const codigoSeguridad = String(facturaData.codigo_seguridad || '').trim();
  const fechaFirma = String(facturaData.fecha_firma || '').trim();

  // URL de consulta de timbre de la DGII (misma que codifica fac1030/procesarqr).
  // Ambiente de la emisión si viene; si no, el del tenant (varinicial.ambienteecf).
  const ambienteTenant = useAmbienteEcf();
  const ambienteQr = ambienteEcfValido(facturaData.ambiente) || ambienteTenant;

  const qrUrl = useMemo(() => {
    if (!esElectronico || !ambienteQr) return '';
    const ncfQr = String(ncf || '').toUpperCase();
    const codigo = String(facturaData.codigocomprobante ?? '').trim();
    const esConsumo = ncfQr.slice(0, 3) === 'E32' || codigo === '32';
    const rncEmisor = rncEmpresaContexto || '';
    const rncCompradorQr = rncComprador.split('-')[0] || '';
    const montoFuente = facturaData.monto_total_ecf ?? (total || 0);
    const montoTotal = Number(montoFuente).toFixed(2);
    // FechaEmision del e-CF = día de la firma (dd-mm-aaaa), como el QR de las
    // notas. La fecha de la factura puede diferir: antes del arreglo de hora
    // de RD, los e-CF emitidos después de las 8:00 p.m. salían con el día
    // siguiente (E310000000072 de Mendez) y el QR no cuadraba con la DGII.
    const diaFirma = /^(\d{2}-\d{2}-\d{4})/.exec(fechaFirma);
    const fechaEmisionDgiiFmt = diaFirma ? diaFirma[1] : fechaEmisionDgii(fecha);
    if (esConsumo && Number(montoFuente) < 250000) {
      return (
        `https://fc.dgii.gov.do/${ambienteQr}/consultatimbrefc?` +
        `rncemisor=${encodeURIComponent(rncEmisor.replace('-', '').replace('-', ''))}` +
        `&encf=${encodeURIComponent(ncfQr)}` +
        `&montototal=${encodeURIComponent(montoTotal)}` +
        `&codigoseguridad=${encodeURIComponent(codigoSeguridad)}`
      );
    }
    return (
      `https://ecf.dgii.gov.do/${ambienteQr}/ConsultaTimbre?` +
      `RncEmisor=${encodeURIComponent(rncEmisor.replace('-', '').replace('-', ''))}` +
      `&RncComprador=${encodeURIComponent(rncCompradorQr)}` +
      `&ENCF=${encodeURIComponent(ncfQr)}` +
      `&FechaEmision=${encodeURIComponent(fechaEmisionDgiiFmt)}` +
      `&MontoTotal=${encodeURIComponent(montoTotal)}` +
      `&FechaFirma=${encodeURIComponent(fechaFirma)}` +
      `&CodigoSeguridad=${encodeURIComponent(codigoSeguridad)}`
    );
  }, [esElectronico, ncf, facturaData.codigocomprobante, facturaData.monto_total_ecf, rncComprador, total, fecha, rncEmpresaContexto, codigoSeguridad, fechaFirma, ambienteQr]);

  // Genera el QR como data URL (funciona en la vista previa y en la impresion).
  const [qrDataUrl, setQrDataUrl] = useState('');
  useEffect(() => {
    if (!open || !esElectronico || !qrUrl) {
      setQrDataUrl('');
      return undefined;
    }
    let cancelado = false;
    QRCode.toDataURL(qrUrl, { margin: 1, width: 220 })
      .then((url) => {
        if (!cancelado) setQrDataUrl(url);
      })
      .catch(() => {
        if (!cancelado) setQrDataUrl('');
      });
    return () => {
      cancelado = true;
    };
  }, [open, esElectronico, qrUrl]);

  useEffect(() => {
    if (!open) return;
    // Si el invocador (p.ej. FacturaCredito o MantenimientoFacturasEmitidas)
    // ya resolvió la configuración aplicando su propia estrategia de fallback,
    // la usamos tal cual y evitamos un fetch que podría sobrescribirla con la
    // configuración de un tipo distinto.
    if (facturaData.configuracion && typeof facturaData.configuracion === 'object') {
      setConfiguracion({ ...CONFIGURACION_DEFAULT, ...facturaData.configuracion });
      setCargandoConfig(false);
      return undefined;
    }
    const idEmpresa = facturaData.id_empresa;
    const dbEmpresa = idEmpresa && typeof idEmpresa === 'object' ? idEmpresa.db_empresa : null;
    if (!dbEmpresa) {
      setConfiguracion(CONFIGURACION_DEFAULT);
      return undefined;
    }
    // `tipo_documento` debe coincidir con la clave usada por ConfiguracionFactura
    // (nombre del documento fiscal seleccionado, p.ej. "Factura",
    // "Factura de Crédito Fiscal"). Como el nombre que viaja desde los distintos
    // callers puede no coincidir literalmente con la fila guardada, intentamos
    // en cascada igual que FacturaCredito.jsx / MantenimientoFacturasEmitidas.jsx:
    //   1) nombre tal cual
    //   2) nombre en MAYÚSCULAS
    //   3) genéricos: "FACTURA", "FACTURA POS", "Factura"
    // Si alguna respuesta trae `configuracionGuardada === true` la usamos
    // (esa es la plantilla configurada para el documento). Si ninguna coincide
    // explícitamente, conservamos la última respuesta del backend (valores por
    // defecto) en vez de descartarla.
    const nombreTipoTrim =
      (facturaData.tipoDocumento || facturaData.tipo_documento || 'Factura').trim() || 'Factura';
    const intentos = [nombreTipoTrim];
    const upper = nombreTipoTrim.toUpperCase();
    if (upper !== nombreTipoTrim) intentos.push(upper);
    for (const fb of ['FACTURA', 'FACTURA POS', 'Factura']) {
      if (!intentos.includes(fb)) intentos.push(fb);
    }

    let cancelado = false;
    setCargandoConfig(true);
    (async () => {
      let configuracionGuardada = null;
      let ultimaRespuesta = null;
      for (const intento of intentos) {
        try {
          const res = await fetch(servidor + '/api/facturabackend_obtenerconfiguracion', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id_empresa: idEmpresa,
              usuario: usuarioLog || '',
              password: claveLog || '',
              tipo_documento: intento,
            }),
          });
          if (!res.ok) continue;
          const data = await res.json().catch(() => null);
          if (!data) continue;
          if (data.configuracion) ultimaRespuesta = data.configuracion;
          if (data.configuracionGuardada && data.configuracion) {
            configuracionGuardada = data.configuracion;
            break;
          }
        } catch (_) {
          // intento siguiente
        }
      }
      if (cancelado) return;
      const elegida = configuracionGuardada || ultimaRespuesta;
      setConfiguracion(
        elegida ? { ...CONFIGURACION_DEFAULT, ...elegida } : CONFIGURACION_DEFAULT
      );
      setCargandoConfig(false);
    })();

    return () => {
      cancelado = true;
    };
  }, [
    open,
    facturaData.configuracion,
    facturaData.id_empresa,
    facturaData.tipoDocumento,
    facturaData.tipo_documento,
    usuarioLog,
    claveLog,
    servidor,
  ]);

  /**
   * Configuración resuelta: prioriza nombre / dirección / RNC del contexto
   * (login). Si el contexto no los trae, se conservan los de la configuración
   * guardada (Configuración de factura) para que la impresión no quede sin
   * datos de empresa.
   */
  const configuracionResuelta = {
    ...configuracion,
    nombreEmpresa: nombreEmpresaContexto || configuracion.nombreEmpresa || '',
    direccionEmpresa: direccionEmpresaContexto || configuracion.direccionEmpresa || '',
    rncEmpresa: rncEmpresaContexto || configuracion.rncEmpresa || '',
    telefonoEmpresa: telefonoEmisor || configuracion.telefonoEmpresa || '',
  };

  const subtotalAntesDescuentos = items.reduce(
    (s, i) => s + parseFloat(i.cantidad || 0) * parseFloat(i.precio || 0),
    0
  );

  const handleImprimir = () => {
    if (cargandoConfig) {
      showError('Cargando configuración de impresión, intente nuevamente en un momento');
      return;
    }
    const ventana = window.open('', '_blank');
    if (!ventana) {
      showError('Permita ventanas emergentes para imprimir');
      return;
    }
    const plantillaSeleccionada =
      (configuracionResuelta.plantillaPorDefecto || '').trim() || '80mm-basica';
    const esTicket80mm = plantillaSeleccionada.startsWith('80mm');

    let htmlContent;
    if (esTicket80mm) {
      htmlContent = obtenerPlantilla(plantillaSeleccionada, {
        ...facturaData,
        rnc: rncComprador,
        cliente_nombre: clienteNombre,
        configuracion: configuracionResuelta,
        totalNotaCredito,
        totalAPagar,
        mostrarComprobanteElectronico: esElectronico,
        fechaVencimiento,
        qrDataUrl: esElectronico ? qrDataUrl : '',
        codigoSeguridad: esElectronico ? codigoSeguridad : '',
        fechaFirma: esElectronico ? fechaFirma : '',
        usuario: usuarioLog || facturaData.usuario || '',
      });
       
    } else {
      const filasTotales = [
        { label: 'Subtotal', value: subtotalAntesDescuentos },
      ];
      if (totalDescuentos > 0) {
        filasTotales.push({ label: 'Descuentos', value: `−${money(totalDescuentos)}`, danger: true });
      }
      
      filasTotales.push({ label: 'Subtotal neto', value: subtotal });
      if (montoExento > 0) filasTotales.push({ label: 'Monto exento', value: montoExento });
      if (impuestos.length > 0) {
        impuestos.forEach((imp) => {
          filasTotales.push({ label: imp.nombre || 'Impuesto', value: Number(imp.monto || 0) });
        });
        filasTotales.push({ label: 'Total impuestos', value: totalImpuestos });
      }
      if (totalNotaCredito > 0) {
        filasTotales.push({
          label: 'Nota(s) de crédito',
          value: `−${money(totalNotaCredito)}`,
          danger: true,
        });
      }
      const metaExtra = [];
      if (configuracionResuelta.mostrarNCF !== false && ncf) {
        metaExtra.push({ label: 'NCF', value: ncf });
      }
      htmlContent = generarHtmlDocumentoEmpresarial({
        tituloDocumento: facturaData.tituloDocumento || tipoDocumento || 'FACTURA',
        numero,
        documento,
        fecha,
        fechaVencimiento,
        metaExtra,
        parte: parteCliente,
        items,
        filasTotales,
        totalLabel: 'TOTAL',
        totalValue: totalAPagar,
        observaciones: configuracionResuelta.mostrarObservaciones !== false ? observaciones : '',
        qrDataUrl: esElectronico ? qrDataUrl : '',
        codigoSeguridad: esElectronico ? codigoSeguridad : '',
        fechaFirma: esElectronico ? fechaFirma : '',
        mensajeGracias: configuracionResuelta.mensajeGracias || '',
        leyendaPiePagina: configuracionResuelta.leyendaPiePagina || '',
        nombreEmpresa: configuracionResuelta.nombreEmpresa || 'Empresa',
        direccionEmpresa: configuracionResuelta.direccionEmpresa || '',
        telefonoEmpresa: configuracionResuelta.telefonoEmpresa || empresa?.telefono || '',
        rncEmpresa: configuracionResuelta.rncEmpresa || '',
        emailEmpresa: configuracionResuelta.emailEmpresa || '',
        mostrarInfoEmpresa: configuracionResuelta.mostrarInfoEmpresa !== false,
      });
    }
    ventana.document.write(htmlContent);
    ventana.document.close();
    ventana.focus();
    setTimeout(() => {
      ventana.print();
      ventana.close();
    }, 250);
  };

  const abrirDialogoEmail = () => setDialogoEmailAbierto(true);
  const cerrarDialogoEmail = () => {
    setDialogoEmailAbierto(false);
    setEmailDestino('');
  };

  const handleEnviarEmail = async () => {
    if (!usuarioLog) {
      showError('Sesión inválida: falta usuario de login');
      return;
    }
    const email = (emailDestino || '').trim();
    if (!email) {
      showError('Ingrese el correo de destino');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      showError('Correo electrónico no válido');
      return;
    }
    setEnviandoEmail(true);
    try {
      const response = await fetch(servidor + '/api/facturabackend_enviarfactura_email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: facturaData.id_empresa,
          usuario: usuarioLog || '',
          password: claveLog || '',
          email_destino: email,
          factura: {
            items,
            impuestos,
            subtotal,
            totalDescuentos,
            totalImpuestos,
            total,
            totalNotaCredito,
            totalAPagar,
            documento,
            numero,
            fecha,
            ncf,
            tipoDocumento,
            observaciones,
            empresa: nombreEmpresaContexto,
            direccionEmpresa: direccionEmpresaContexto,
            rncEmpresa: rncEmpresaContexto,
          },
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok && !data.Error) {
        showSuccess('Factura enviada por correo correctamente');
        cerrarDialogoEmail();
      } else {
        showError(data.Error || 'No se pudo enviar el correo. Compruebe la configuración del servidor.');
      }
    } catch {
      showError('Error de conexión al enviar el correo');
    } finally {
      setEnviandoEmail(false);
    }
  };

  const filasTotalesPreview = [
    { label: 'Subtotal', value: subtotalAntesDescuentos },
  ];
  if (totalDescuentos > 0) {
    filasTotalesPreview.push({
      label: 'Descuentos',
      value: `−${money(totalDescuentos)}`,
      danger: true,
    });
  }
  filasTotalesPreview.push({ label: 'Subtotal neto', value: subtotal });
  if (montoExento > 0) filasTotalesPreview.push({ label: 'Monto exento', value: montoExento });
  if (impuestos.length > 0) {
    impuestos.forEach((imp) => {
      filasTotalesPreview.push({ label: imp.nombre || 'Impuesto', value: Number(imp.monto || 0) });
    });
    filasTotalesPreview.push({ label: 'Total impuestos', value: totalImpuestos });
  }
  if (totalNotaCredito > 0) {
    filasTotalesPreview.push({
      label: 'Nota(s) de crédito',
      value: `−${money(totalNotaCredito)}`,
      danger: true,
    });
  }

  const metaExtraPreview = [];
  if (configuracionResuelta.mostrarNCF !== false && ncf) {
    metaExtraPreview.push({ label: 'NCF', value: ncf });
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            boxShadow: FP.shadow.md,
            maxHeight: '92vh',
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            px: { xs: 2, md: 3 },
            py: 1.6,
            borderBottom: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
            display: 'flex',
            alignItems: 'center',
            gap: 1.2,
          }}
        >
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: 1.2,
              bgcolor: FP.color.navySoft,
              color: '#FFFFFF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <DescriptionOutlinedIcon sx={{ fontSize: 18 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary, fontSize: '0.98rem', lineHeight: 1.2 }}>
              Factura {documento || 'Pendiente'}-{numero || '—'}
            </Typography>
            <Typography sx={{ color: FP.color.textMuted, fontSize: '0.72rem' }}>
              Vista previa · Formato empresarial
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }} />
          <Button
            startIcon={<CloseIcon />}
            onClick={onClose}
            size="small"
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              color: FP.color.textSecondary,
              borderRadius: 1.4,
              '&:hover': { bgcolor: FP.color.surfaceAlt },
            }}
          >
            Cerrar
          </Button>
        </DialogTitle>

        <DialogContent sx={{ p: { xs: 2, md: 3 }, bgcolor: FP.color.surface }}>
          <DocumentoEmpresarialPreview
            printRef={printRef}
            mostrarInfoEmpresa={configuracionResuelta.mostrarInfoEmpresa !== false}
            nombreEmpresa={configuracionResuelta.nombreEmpresa || 'Empresa'}
            direccionEmpresa={configuracionResuelta.direccionEmpresa || ''}
            telefonoEmpresa={configuracionResuelta.telefonoEmpresa || empresa?.telefono || ''}
            rncEmpresa={configuracionResuelta.rncEmpresa || ''}
            emailEmpresa={configuracionResuelta.emailEmpresa || ''}
            tituloDocumento={facturaData.tituloDocumento || tipoDocumento || 'FACTURA'}
            numero={numero}
            documento={documento}
            fecha={fecha}
            fechaVencimiento={fechaVencimiento}
            metaExtra={metaExtraPreview}
            parte={parteCliente}
            items={items}
            filasTotales={filasTotalesPreview}
            totalLabel="TOTAL"
            totalValue={totalAPagar}
            observaciones={
              configuracionResuelta.mostrarObservaciones !== false ? observaciones : ''
            }
            qrDataUrl={esElectronico ? qrDataUrl : ''}
            codigoSeguridad={esElectronico ? codigoSeguridad : ''}
            fechaFirma={esElectronico ? fechaFirma : ''}
            mensajeGracias={configuracionResuelta.mensajeGracias || ''}
            footerNote={configuracionResuelta.leyendaPiePagina || ''}
            varianteTabla="listado"
          />
        </DialogContent>

        <DialogActions
          sx={{
            px: { xs: 2, md: 3 },
            py: 1.5,
            borderTop: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
            gap: 1,
          }}
        >
          <Button
            startIcon={<PrintIcon />}
            onClick={handleImprimir}
            disabled={cargandoConfig}
            variant="outlined"
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 1.4,
              borderColor: FP.color.border,
              color: FP.color.textPrimary,
              '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
            }}
          >
            {cargandoConfig ? 'Cargando formato…' : 'Imprimir'}
          </Button>
          <Button
            startIcon={<EmailIcon />}
            onClick={abrirDialogoEmail}
            variant="outlined"
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 1.4,
              borderColor: FP.color.border,
              color: FP.color.textPrimary,
              '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
            }}
          >
            Enviar por email
          </Button>
          <Button
            onClick={onClose}
            variant="contained"
            sx={{
              textTransform: 'none',
              borderRadius: 1.4,
              fontWeight: 700,
              bgcolor: FP.color.navy,
              color: '#FFFFFF',
              boxShadow: 'none',
              '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
            }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={dialogoEmailAbierto}
        onClose={cerrarDialogoEmail}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            boxShadow: FP.shadow.md,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            px: { xs: 2, md: 3 },
            py: 1.6,
            borderBottom: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
            fontWeight: 700,
            color: FP.color.textPrimary,
            fontSize: '0.98rem',
          }}
        >
          Enviar factura por correo
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2, md: 3 } }}>
          <TextField
            autoFocus
            fullWidth
            label="Correo electrónico"
            type="email"
            value={emailDestino}
            onChange={(e) => setEmailDestino(e.target.value)}
            placeholder="ejemplo@correo.com"
            disabled={enviandoEmail || !usuarioLog}
            InputLabelProps={{ shrink: true }}
            sx={{ mt: 1, ...fpInputSx }}
          />
        </DialogContent>
        <DialogActions
          sx={{
            px: { xs: 2, md: 3 },
            py: 1.5,
            borderTop: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
            gap: 1,
          }}
        >
          <Button
            onClick={cerrarDialogoEmail}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              color: FP.color.textSecondary,
              borderRadius: 1.4,
              '&:hover': { bgcolor: FP.color.surfaceAlt },
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleEnviarEmail}
            variant="contained"
            disabled={enviandoEmail || !usuarioLog}
            sx={{
              textTransform: 'none',
              borderRadius: 1.4,
              fontWeight: 700,
              bgcolor: FP.color.navy,
              color: '#FFFFFF',
              boxShadow: 'none',
              '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
              '&.Mui-disabled': { bgcolor: FP.color.border, color: FP.color.textMuted },
            }}
          >
            {enviandoEmail ? 'Enviando…' : 'Enviar'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default VistaFactura;
